/**
 * useIdleTimer Hook
 * Detects user inactivity and manages idle timeout with cross-tab synchronization
 * Uses absolute deadline approach for robust countdown
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export default function useIdleTimer({
  idleTimeoutMs,
  warningSeconds = 60,
  onShowWarning,
  onHideWarning,
  onAutoLogout,
  heartbeatMs = 60000,
  enabled = true,
}) {
  const lastActivityRef = useRef(Date.now());
  const warningTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const logoutAtRef = useRef(null); // absolute epoch ms for when auto-logout will occur
  const heartbeatRef = useRef(0);
  const channelRef = useRef(null);
  // Note: countdown is now handled by useRealTimeCountdown hook in the modal
  const [warningVisible, setWarningVisible] = useState(false);
  const [isQuizActive, setIsQuizActive] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const timersScheduledRef = useRef(false);

  // Cross-tab communication using BroadcastChannel with localStorage fallback
  const bcSend = useCallback((type, payload = {}) => {
    try {
      if (channelRef.current) {
        channelRef.current.postMessage({ type, payload });
      }
    } catch (error) {
      console.warn('BroadcastChannel not available:', error);
    }
    
    try {
      localStorage.setItem('idle-sync', JSON.stringify({ 
        t: Date.now(), 
        type, 
        payload 
      }));
    } catch (error) {
      console.warn('localStorage not available:', error);
    }
  }, []);

  // Check if user is on a quiz page
  const checkQuizActivity = useCallback(() => {
    const currentPath = window.location.pathname;
    const isOnQuizPage = currentPath.includes('/student/attempt/') || 
                        currentPath.includes('/student/assignments/') ||
                        currentPath.includes('/quiz/');
    
    setIsQuizActive(isOnQuizPage);
    return isOnQuizPage;
  }, []);

  // Clear countdown interval helper
  const clearCountdownInterval = useCallback(() => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  // Start countdown interval with absolute deadline approach
  const startCountdownInterval = useCallback(() => {
    clearCountdownInterval();
    
    const tick = () => {
      const now = Date.now();
      const remainingMs = Math.max(0, (logoutAtRef.current || now) - now);
      // Countdown is now handled by useRealTimeCountdown hook
      // const next = Math.ceil(remainingMs / 1000); // Unused since countdown moved to modal
      
      if (remainingMs <= 0) {
        clearCountdownInterval();
      }
    };
    
    tick(); // immediate render without waiting
    countdownIntervalRef.current = setInterval(tick, 250); // smoother & resilient to clamping
  }, [clearCountdownInterval]);

  // Schedule warning and logout timers
  const scheduleTimers = useCallback(() => {
    // Don't schedule timers if not enabled
    if (!enabled) {
      return;
    }
    
    // Don't schedule timers if paused (e.g., during active quiz)
    if (isPaused) {
      return;
    }
    
    // Don't schedule timers if they're already scheduled
    if (timersScheduledRef.current) {
      return;
    }
    
    timersScheduledRef.current = true;
    
    // Clear existing timers (but don't clear countdown if warning is visible)
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
    }
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
    }
    if (countdownIntervalRef.current && !warningVisible) {
      clearCountdownInterval();
    }

    // Calculate when to show warning (idleTimeout - warningSeconds)
    const warnAt = Math.max(0, idleTimeoutMs - warningSeconds * 1000);
    
    // Set warning timer
    warningTimerRef.current = setTimeout(() => {
      // Double-check if we're still not paused before showing warning
      if (!isPaused) {
        setWarningVisible(true);
        onShowWarning?.();
        
        // Establish absolute logout deadline and share it across tabs
        logoutAtRef.current = Date.now() + (warningSeconds * 1000);
        bcSend('warning-start', { logoutAt: logoutAtRef.current });
        startCountdownInterval();
      }
    }, warnAt);

    // Set logout timer
    logoutTimerRef.current = setTimeout(() => {
      // Double-check if we're still not paused before auto-logout
      if (!isPaused) {
        setWarningVisible(false);
        onHideWarning?.();
        clearCountdownInterval();
        onAutoLogout?.();
        bcSend('auto-logout');
      }
    }, idleTimeoutMs);
  }, [idleTimeoutMs, warningSeconds, onShowWarning, onHideWarning, onAutoLogout, bcSend, isPaused, enabled, warningVisible, clearCountdownInterval, startCountdownInterval]);

  // Pause idle timer (e.g., during active quiz)
  const pause = useCallback(() => {
    setIsPaused(true);
    clearTimeout(warningTimerRef.current);
    clearTimeout(logoutTimerRef.current);
    clearCountdownInterval();
    setWarningVisible(false);
    timersScheduledRef.current = false;
    onHideWarning?.();
    bcSend('pause');
  }, [onHideWarning, bcSend, clearCountdownInterval]);

  // Resume idle timer (e.g., after quiz completion)
  const resume = useCallback(() => {
    setIsPaused(false);
    lastActivityRef.current = Date.now();
    scheduleTimers();
    bcSend('resume');
  }, [scheduleTimers, bcSend]);

  // Reset timers when user is active
  const reset = useCallback(() => {
    lastActivityRef.current = Date.now();
    setWarningVisible(false);
    // Countdown is now handled by useRealTimeCountdown hook
    timersScheduledRef.current = false;
    onHideWarning?.();
    clearCountdownInterval();
    scheduleTimers();
    bcSend('reset');
  }, [scheduleTimers, onHideWarning, bcSend, clearCountdownInterval]);

  // Report user activity and handle heartbeat
  const reportActivity = useCallback(() => {
    const now = Date.now();
    
    // Check if we're on a quiz page and update state accordingly
    const isOnQuizPage = checkQuizActivity();
    
    // If we're on a quiz page, pause the idle timer
    if (isOnQuizPage && !isPaused) {
      pause();
      return;
    }
    
    // If we're not on a quiz page but were paused, resume
    if (!isOnQuizPage && isPaused) {
      resume();
      return;
    }
    
    // If warning is visible or countdown is running, don't reset the countdown - let it continue counting down
    if (warningVisible || countdownIntervalRef.current) {
      return;
    }
    
    // Only update if there's been significant activity (avoid spam)
    if (now - lastActivityRef.current > 1000) {
      lastActivityRef.current = now;
      // Reset the timer when there's user activity
      timersScheduledRef.current = false;
      scheduleTimers();
    }

    // Send heartbeat to server if enough time has passed
    const sinceHeartbeat = now - heartbeatRef.current;
    if (sinceHeartbeat > heartbeatMs) {
      heartbeatRef.current = now;
      
      // Send keepalive request to server
      const token = localStorage.getItem('faithinsight_token');
      if (token) {
        fetch('/portal/api/auth/keepalive', { 
          method: 'POST', 
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }).catch(error => {
          console.warn('Keepalive request failed:', error);
        });
      }
    }
  }, [heartbeatMs, checkQuizActivity, isPaused, pause, resume, warningVisible, scheduleTimers]);

  // Stay logged in - reset timers and ping server
  const stayLoggedIn = useCallback(async () => {
    try {
      // Send keepalive request to server
      const token = localStorage.getItem('faithinsight_token');
      if (token) {
        await fetch('/portal/api/auth/keepalive', { 
          method: 'POST', 
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
      }
    } catch (error) {
      console.warn('Keepalive request failed:', error);
    }
    
    reset();
    bcSend('stay');
  }, [reset, bcSend]);

  // Handle cross-tab messages
  useEffect(() => {
    // Initialize BroadcastChannel
    if ('BroadcastChannel' in window) {
      channelRef.current = new BroadcastChannel('idle-sync');
      
      channelRef.current.addEventListener('message', (event) => {
        const { type, payload } = event.data || {};
        
        if (type === 'warning-start' && payload?.logoutAt) {
          // Align every tab to same absolute deadline
          logoutAtRef.current = payload.logoutAt;
          setWarningVisible(true);
          onShowWarning?.();
          startCountdownInterval();
        }
        if (type === 'reset' || type === 'stay') {
          reset();
        }
        if (type === 'auto-logout') {
          onAutoLogout?.();
        }
        if (type === 'pause') {
          pause();
        }
        if (type === 'resume') {
          resume();
        }
      });
    }

    // Handle localStorage changes (fallback for cross-tab sync)
    const handleStorageChange = (event) => {
      if (event.key === 'idle-sync') {
        try {
          const { type, payload } = JSON.parse(event.newValue || '{}');
          
          if (type === 'warning-start' && payload?.logoutAt) {
            logoutAtRef.current = payload.logoutAt;
            setWarningVisible(true);
            onShowWarning?.();
            startCountdownInterval();
          }
          if (type === 'reset' || type === 'stay') {
            reset();
          }
          if (type === 'auto-logout') {
            onAutoLogout?.();
          }
          if (type === 'pause') {
            pause();
          }
          if (type === 'resume') {
            resume();
          }
        } catch (error) {
          console.warn('Error parsing idle-sync data:', error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Set up activity event listeners
    const events = [
      'mousemove',
      'mousedown', 
      'keydown',
      'touchstart',
      'scroll',
      'visibilitychange'
    ];

    const activityHandler = () => {
      reportActivity();
    };

    // Add event listeners with passive option for better performance
    events.forEach(eventType => {
      window.addEventListener(eventType, activityHandler, { passive: true });
    });

    // Add route change detection
    const handleRouteChange = () => {
      // Check quiz activity when route changes
      setTimeout(() => {
        checkQuizActivity();
      }, 100);
    };

    // Listen for route changes (for React Router)
    window.addEventListener('popstate', handleRouteChange);
    
    // Also check on initial load
    checkQuizActivity();

    // Initialize timers
    scheduleTimers();

    // Cleanup function
    return () => {
      // Clear timers
      clearTimeout(warningTimerRef.current);
      clearTimeout(logoutTimerRef.current);
      clearCountdownInterval();

      // Remove event listeners
      events.forEach(eventType => {
        window.removeEventListener(eventType, activityHandler);
      });
      
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('popstate', handleRouteChange);
      
      // Close BroadcastChannel
      if (channelRef.current) {
        channelRef.current.close();
      }
    };
  }, [reportActivity, reset, onAutoLogout, checkQuizActivity, pause, resume, scheduleTimers, onShowWarning, startCountdownInterval, clearCountdownInterval]);

  // Handle enabled state changes
  useEffect(() => {
    if (!enabled) {
      // Clear all timers when disabled
      clearTimeout(warningTimerRef.current);
      clearTimeout(logoutTimerRef.current);
      clearCountdownInterval();
      setWarningVisible(false);
      // Countdown is now handled by useRealTimeCountdown hook
      timersScheduledRef.current = false;
    } else {
      // Re-initialize timers when enabled
      scheduleTimers();
    }
  }, [enabled, scheduleTimers, warningSeconds, clearCountdownInterval]);

  return {
    warningVisible,
    logoutAtMs: warningVisible ? logoutAtRef.current : null,
    reset,
    stayLoggedIn,
    pause,
    resume,
    isPaused,
    isQuizActive
  };
}